import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import MGAAPI from "../../../api/index";
import { AccordionItem } from "../../AccordianItem";
import { addedStepsAtom } from "../../../utils/atoms/addedStepsAtom";
import { TextInput, useMantineColorScheme } from "@mantine/core";
const MGA_API = new MGAAPI();
function PVIncentive({ updateData, form, componentDetails, incentivesData }) {
  const { t } = useTranslation();
  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const { colorScheme } = useMantineColorScheme();

  const [visibleDetails, setVisibleDetails] = useState({});
  const [incentives, setIncentives] = useState([]); // Keep incentives in local state

  useEffect(() => {
    if (incentivesData) {
      setIncentives(incentivesData);
    }
  }, [incentivesData]);
  
  // Toggle row details visibility
  const toggleDetailVisibility = (id) => {
    setVisibleDetails((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  // Handle input changes and store them in global state (without modifying API data)
  const handleInputChange = (incentiveId, attributeId, value) => {
    const updatedIncentives = incentives?.map((item) =>
      item.incentiveTemplateId === incentiveId
        ? {
            ...item,
            incentiveAttributes: item.incentiveAttributes.map((attr) =>
              attr.incentiveTemplateAttributeId === attributeId
                ? { ...attr, attributeDefaultValue: value }
                : attr
            ),
          }
        : item
    );
    setIncentives(updatedIncentives);
    updateData(updatedIncentives);
  };

  const handleCheckboxChange = (event, incentiveId) => {
    const {
      target: { selected },
    } = { ...event };

    const updatedIncentivesStatus = incentives?.map((item) =>
      item.incentiveTemplateId === incentiveId
        ? {
            ...item,
            status: selected,
          }
        : item
    );
    setIncentives(updatedIncentivesStatus);
    updateData(updatedIncentivesStatus);
  };

  const getIncentiveValidationState = (item, inc) => {
    const valueStr = (inc?.attributeDefaultValue ?? "").toString().trim();
    const isRequiredInvalid = !!item?.status && valueStr === "";

    let isPatternInvalid = false;
    if (item?.status && !isRequiredInvalid && inc?.attributeRegex) {
      try {
        const regex = new RegExp(inc.attributeRegex);
        isPatternInvalid = !regex.test(valueStr);
      } catch (e) {
        isPatternInvalid = false;
      }
    }

    const isInvalid = isRequiredInvalid || isPatternInvalid;
    const errorMessage = isRequiredInvalid
      ? t("common.value.required")
      : isPatternInvalid
      ? inc?.attributeRegexMessage || t("common.value.invalidFormat")
      : null;

    return { isInvalid, isRequiredInvalid, isPatternInvalid, errorMessage };
  };

  const header = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("pv") + 1}: E.`}
      </span>
      &nbsp;<span
        className={`ml-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("PVIncentive")}
      </span>
    </>
  );
  const children = () => (
    <div className="p-10px">
      <se-block height="400px">
        <se-table>
          <se-table-group-header>
            <se-table-item-header width="60px"></se-table-item-header>
            <se-table-item-header width="53px"></se-table-item-header>
            <se-table-item-header
              resizable="true"
              flex="1"
              max-width="200px"
              sort="asc"
            >
              {t("pv.incentive.type")}
            </se-table-item-header>
            <se-table-item-header
              flex="3"
              // max-width="600px"
              clickable="true"
              resizable="true"
            >
              {t("pv.incentive.description")}
            </se-table-item-header>
            {/* <se-table-item-header flex="1" min-width="100px" resizable="true">
              {t("pv.incentive.status")}
            </se-table-item-header> */}
          </se-table-group-header>
          {incentives?.map((item) => (
            <se-table-group selectable="true" key={item.incentiveTemplateId}>
              <se-table-item width="53px">
                <se-icon
                  onClick={() =>
                    toggleDetailVisibility(item.incentiveTemplateId)
                  }
                  option="button"
                >
                  {item?.status || visibleDetails[item.incentiveTemplateId]
                    ? "arrow2_up"
                    : "arrow2_down"}
                </se-icon>
              </se-table-item>
              <se-table-item width="60px">
                <se-checkbox
                  selected={item?.status}
                  onClick={(e) =>
                    handleCheckboxChange(e, item.incentiveTemplateId)
                  }
                ></se-checkbox>
              </se-table-item>
              <se-table-item max-width="200px">
                <span className="text-wrap">{item.incentiveName}</span>
              </se-table-item>
              <se-table-item>
                <span className="text-wrap" max-width="600px">
                  {item.incentiveDescription}
                </span>
              </se-table-item>
              {/* <se-table-item>
                <span className="text-wrap">
                  {item?.incentiveComputationFormula || t("common.notAvailable")}
                </span>
              </se-table-item> */}
              {(item?.status || visibleDetails[item.incentiveTemplateId]) && (
                <div slot="detail">
                  {item?.incentiveAttributes?.map((inc) => (
                    <se-table-item key={inc.incentiveTemplateAttributeId}>
                      <div className="row align-items-center">
                        <div className="col-sm-3">
                          <div className="form-group row m-0 p-0">
                            <div className="col-sm-6 px-0 d-flex justify-content-end">
                              <label
                                htmlFor={`input-${inc.incentiveTemplateAttributeId}`}
                                className="col-form-label text-wrap bold se-font-14px-bold"
                              >
                                {inc.attributeLabelName}
                              </label>
                              <span className="se-mandatory pl-2 pt-2 ">*</span>
                            </div>
                            <div className="col-sm-6">
                              {(() => {
                                const validation = getIncentiveValidationState(item, inc);
                                return (
                                  <TextInput
                                id={`input-${inc.incentiveTemplateAttributeId}`}
                                type="text"
                                className={!item?.status ? "opacity-50" : ""}
                                name={inc.attributeLabelName}
                                placeholder={t("pv.placeholder.addValue")}
                                value={inc.attributeDefaultValue ?? ""}
                                disabled={!item?.status}
                                required={!!item?.status}
                                aria-required={!!item?.status}
                                aria-invalid={item?.status ? validation.isInvalid : false}
                                aria-describedby={`error-${inc.incentiveTemplateAttributeId}`}
                                onChange={(e) =>
                                  handleInputChange(
                                    item.incentiveTemplateId,
                                    inc.incentiveTemplateAttributeId,
                                    e.target.value
                                  )
                                }
                                error={item?.status && validation.isInvalid ? validation.errorMessage : null}
                              />
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <span className="text-wrap">
                            {inc.attributeDescription}
                          </span>
                        </div>
                      </div>
                    </se-table-item>
                  ))}
                </div>
              )}
            </se-table-group>
          ))}
        </se-table>
      </se-block>
    </div>
  );

  // --- Validation helper & effect to expose error state upstream ---
  const hasIncentiveErrors = (current = incentives) => {
    return current?.some((item) => {
      if (!item?.status) return false; // only validate selected incentives
      return item?.incentiveAttributes?.some((inc) => {
        const valueStr = (inc.attributeDefaultValue ?? "").toString().trim();
        const isRequiredInvalid = valueStr === ""; // empty only
        if (isRequiredInvalid) return true;
        if (inc.attributeRegex) {
          try {
            const regex = new RegExp(inc.attributeRegex);
            return !regex.test(valueStr);
          } catch (e) {
            // bad regex -> ignore pattern failure
            return false;
          }
        }
        return false;
      });
    });
  };

  useEffect(() => {
    if (!form) return; // safety if form not passed
    if (hasIncentiveErrors()) {
      // Set a single form-level error to block save
      form.setFieldError(
        "incentives",
        t("pv.incentive.formError")
      );
    } else {
      // Clear error when resolved
      form.clearFieldError("incentives");
    }
  }, [incentives, form]);

  return (
    <AccordionItem id={3} header={header()} key={3} children={children()} />
  );
}
export default PVIncentive;
